// PolitiqueConfidentialite.jsx
// À intégrer dans la Vitrine_v5.jsx de SecoursPros
// Route suggérée : /confidentialite
// Usage : <PolitiqueConfidentialite /> dans ton router

import { useState } from "react";

const SIREN = "478 863 921";
const EMAIL = "contact@secourspros.fr";
const SITE = "https://secourspros.fr";
const DATE = "14 avril 2026";

const sections = [
  {
    id: "responsable",
    num: "01",
    titre: "Identité du responsable de traitement",
    contenu: () => (
      <>
        <p>La présente politique est établie par :</p>
        <ul>
          <li><strong>Nom commercial :</strong> SecoursPros</li>
          <li><strong>Exploitant :</strong> Auto-entrepreneur — SIREN {SIREN}</li>
          <li><strong>Contact :</strong> <a href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
          <li><strong>Site :</strong> <a href={SITE}>{SITE}</a></li>
          <li><strong>Hébergement :</strong> Netlify Inc. (USA) — clauses contractuelles types conformes au RGPD</li>
        </ul>
      </>
    ),
  },
  {
    id: "donnees",
    num: "02",
    titre: "Données personnelles collectées",
    contenu: () => (
      <>
        <h3>Lors de l'inscription (formulaire Tally)</h3>
        <ul>
          <li>Nom et prénom du référent</li>
          <li>Adresse e-mail professionnelle</li>
          <li>Nom de la structure (mairie, entreprise, crèche…)</li>
          <li>Numéro de téléphone (facultatif)</li>
          <li>Type d'abonnement souhaité</li>
        </ul>
        <h3>Lors de l'utilisation de l'application</h3>
        <ul>
          <li>Identifiant utilisateur et type de profil</li>
          <li>Résultats de quiz et historique de progression</li>
          <li>Appareils enregistrés (identifiant technique, date)</li>
          <li>Géolocalisation (uniquement si activée par l'utilisateur)</li>
        </ul>
        <h3>Lors du paiement (Stripe)</h3>
        <ul>
          <li>Informations de facturation (nom, adresse, e-mail)</li>
          <li>Données bancaires : traitées exclusivement par Stripe — SecoursPros ne stocke aucune donnée de carte</li>
        </ul>
        <h3>Données de navigation</h3>
        <ul>
          <li>Adresse IP anonymisée, type de navigateur, pages visitées</li>
        </ul>
      </>
    ),
  },
  {
    id: "finalites",
    num: "03",
    titre: "Finalités et bases légales",
    contenu: () => (
      <>
        <p>Chaque traitement repose sur une base légale définie par le RGPD :</p>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Finalité</th>
                <th>Base légale</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Création et gestion du compte", "Exécution du contrat"],
                ["Envoi de l'e-mail de bienvenue", "Exécution du contrat"],
                ["Gestion des abonnements et facturation", "Exécution du contrat"],
                ["Alertes de quota d'appareils", "Intérêt légitime"],
                ["Support client (Tidio)", "Intérêt légitime"],
                ["Amélioration de l'application", "Intérêt légitime"],
                ["E-mails commerciaux (clients existants)", "Intérêt légitime"],
                ["Prospection (non-clients)", "Consentement"],
              ].map(([fin, base]) => (
                <tr key={fin}>
                  <td>{fin}</td>
                  <td><span className="badge">{base}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    id: "destinataires",
    num: "04",
    titre: "Destinataires des données",
    contenu: () => (
      <>
        <p>SecoursPros ne vend ni ne loue vos données. Elles peuvent être transmises aux sous-traitants techniques suivants :</p>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr><th>Sous-traitant</th><th>Rôle</th><th>Garantie</th></tr>
            </thead>
            <tbody>
              {[
                ["Supabase Inc.", "Base de données", "CCT + DPA"],
                ["Netlify Inc.", "Hébergement web", "CCT + DPA"],
                ["Resend Inc.", "E-mails transactionnels", "CCT + DPA"],
                ["Stripe Inc.", "Paiements", "PCI DSS"],
                ["Tally.so", "Formulaires", "RGPD (UE)"],
                ["Tidio", "Support client", "RGPD (UE)"],
                ["Make.com", "Automatisation", "CCT + DPA"],
              ].map(([nom, role, garantie]) => (
                <tr key={nom}>
                  <td><strong>{nom}</strong></td>
                  <td>{role}</td>
                  <td><span className="badge badge-green">{garantie}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    id: "conservation",
    num: "05",
    titre: "Durée de conservation",
    contenu: () => (
      <ul>
        <li><strong>Compte actif :</strong> durée de l'abonnement + 3 ans après résiliation</li>
        <li><strong>Données de facturation :</strong> 10 ans (obligation légale)</li>
        <li><strong>Prospects non-clients :</strong> 3 ans depuis le dernier contact</li>
        <li><strong>Données de navigation :</strong> 13 mois maximum</li>
        <li><strong>Conversations support :</strong> 2 ans</li>
      </ul>
    ),
  },
  {
    id: "droits",
    num: "06",
    titre: "Vos droits",
    contenu: () => (
      <>
        <p>Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :</p>
        <div className="droits-grid">
          {[
            ["Accès", "Obtenir une copie de vos données"],
            ["Rectification", "Corriger des données inexactes"],
            ["Effacement", "Demander la suppression de vos données"],
            ["Limitation", "Restreindre l'utilisation de vos données"],
            ["Portabilité", "Recevoir vos données dans un format structuré"],
            ["Opposition", "Vous opposer à un traitement basé sur l'intérêt légitime"],
          ].map(([titre, desc]) => (
            <div className="droit-card" key={titre}>
              <span className="droit-titre">{titre}</span>
              <span className="droit-desc">{desc}</span>
            </div>
          ))}
        </div>
        <p>Pour exercer vos droits : <a href={`mailto:${EMAIL}`}>{EMAIL}</a> — Réponse sous 30 jours.</p>
        <p>En cas de réponse insatisfaisante, vous pouvez saisir la <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">CNIL</a>.</p>
      </>
    ),
  },
  {
    id: "cookies",
    num: "07",
    titre: "Cookies",
    contenu: () => (
      <>
        <p>Le site utilise des <strong>cookies techniques strictement nécessaires</strong> au fonctionnement (authentification, préférences). Ces cookies ne nécessitent pas votre consentement.</p>
        <p>Des cookies de mesure d'audience peuvent être utilisés après votre consentement explicite (bannière lors de la première visite).</p>
        <p>Consultez notre <a href="/cookies">politique de gestion des cookies</a> pour le détail complet.</p>
      </>
    ),
  },
  {
    id: "transferts",
    num: "08",
    titre: "Transferts hors Union Européenne",
    contenu: () => (
      <>
        <p>Certains sous-traitants sont établis aux États-Unis. Ces transferts sont encadrés par :</p>
        <ul>
          <li>Des <strong>clauses contractuelles types (CCT)</strong> approuvées par la Commission européenne</li>
          <li>La certification <strong>EU-US Data Privacy Framework</strong></li>
        </ul>
        <p>Ces garanties assurent un niveau de protection équivalent à celui du RGPD.</p>
      </>
    ),
  },
  {
    id: "securite",
    num: "09",
    titre: "Sécurité des données",
    contenu: () => (
      <ul>
        <li>Chiffrement HTTPS (TLS) sur toutes les communications</li>
        <li>Authentification sécurisée via Supabase Auth</li>
        <li>Row Level Security (RLS) sur la base de données</li>
        <li>Sauvegardes automatiques quotidiennes</li>
        <li>Accès administrateur restreint au seul responsable de traitement</li>
      </ul>
    ),
  },
  {
    id: "modifications",
    num: "10",
    titre: "Modifications",
    contenu: () => (
      <>
        <p>SecoursPros se réserve le droit de modifier cette politique à tout moment. En cas de modification substantielle, vous serez informé par e-mail au moins 30 jours avant l'entrée en vigueur.</p>
        <p>La version en vigueur est toujours accessible à l'adresse : <a href={`${SITE}/confidentialite`}>{SITE}/confidentialite</a></p>
      </>
    ),
  },
];

export default function PolitiqueConfidentialite() {
  const [ouvert, setOuvert] = useState(null);

  return (
    <div className="pc-page">
      <style>{`
        .pc-page {
          max-width: 820px;
          margin: 0 auto;
          padding: 3rem 1.5rem 5rem;
          font-family: 'Segoe UI', system-ui, sans-serif;
          color: #1a1a2e;
        }
        .pc-header {
          margin-bottom: 3rem;
          padding-bottom: 2rem;
          border-bottom: 2px solid #e8f0fe;
        }
        .pc-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #1a3a6b;
          margin: 0 0 0.5rem;
        }
        .pc-header p {
          color: #666;
          font-size: 0.9rem;
          margin: 0;
        }
        .pc-intro {
          background: #eef4ff;
          border-left: 4px solid #e53935;
          border-radius: 0 8px 8px 0;
          padding: 1rem 1.25rem;
          margin-bottom: 2rem;
          font-size: 0.92rem;
          color: #1a3a6b;
        }
        .pc-section {
          border: 1px solid #e0e8f5;
          border-radius: 12px;
          margin-bottom: 1rem;
          overflow: hidden;
          transition: box-shadow 0.2s;
        }
        .pc-section:hover {
          box-shadow: 0 2px 12px rgba(26,58,107,0.08);
        }
        .pc-section-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.25rem;
          cursor: pointer;
          user-select: none;
          background: #fff;
          transition: background 0.15s;
        }
        .pc-section-header:hover {
          background: #f5f8ff;
        }
        .pc-num {
          font-size: 0.75rem;
          font-weight: 700;
          color: #e53935;
          letter-spacing: 0.05em;
          min-width: 28px;
        }
        .pc-section-title {
          flex: 1;
          font-weight: 600;
          font-size: 0.97rem;
          color: #1a3a6b;
        }
        .pc-chevron {
          color: #aaa;
          font-size: 1rem;
          transition: transform 0.2s;
        }
        .pc-chevron.ouvert {
          transform: rotate(180deg);
        }
        .pc-section-body {
          padding: 0 1.25rem 1.25rem;
          border-top: 1px solid #e8f0fe;
          background: #fafcff;
          font-size: 0.9rem;
          line-height: 1.7;
          color: #333;
          animation: slideDown 0.2s ease;
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pc-section-body p { margin: 0.75rem 0 0; }
        .pc-section-body h3 {
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #1a3a6b;
          margin: 1.25rem 0 0.5rem;
        }
        .pc-section-body ul {
          margin: 0.5rem 0;
          padding-left: 1.25rem;
        }
        .pc-section-body li { margin-bottom: 0.3rem; }
        .pc-section-body a {
          color: #e53935;
          text-decoration: none;
          font-weight: 500;
        }
        .pc-section-body a:hover { text-decoration: underline; }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.75rem 0;
          font-size: 0.87rem;
        }
        th {
          background: #1a3a6b;
          color: #fff;
          padding: 0.5rem 0.75rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.8rem;
        }
        td {
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid #e8f0fe;
          vertical-align: middle;
        }
        tr:last-child td { border-bottom: none; }
        tr:nth-child(even) td { background: #f5f8ff; }
        .badge {
          display: inline-block;
          font-size: 0.75rem;
          padding: 2px 8px;
          border-radius: 20px;
          background: #eef4ff;
          color: #1a3a6b;
          font-weight: 500;
        }
        .badge-green {
          background: #e8f5e9;
          color: #2e7d32;
        }
        .droits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
          margin: 0.75rem 0;
        }
        .droit-card {
          background: #fff;
          border: 1px solid #e0e8f5;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }
        .droit-titre {
          font-weight: 700;
          font-size: 0.85rem;
          color: #e53935;
        }
        .droit-desc {
          font-size: 0.82rem;
          color: #555;
        }
        .pc-footer {
          margin-top: 3rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e0e8f5;
          text-align: center;
          font-size: 0.82rem;
          color: #999;
        }
        .pc-footer a { color: #e53935; text-decoration: none; }
        @media (max-width: 600px) {
          .pc-page { padding: 1.5rem 1rem 3rem; }
          .pc-header h1 { font-size: 1.5rem; }
          .droits-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="pc-header">
        <h1>Politique de confidentialité</h1>
        <p>SecoursPros — SIREN {SIREN} — Dernière mise à jour : {DATE}</p>
      </div>

      <div className="pc-intro">
        SecoursPros s'engage à protéger vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679). Cette page vous explique quelles données nous collectons, pourquoi, et quels sont vos droits.
      </div>

      {sections.map((s) => {
        const estOuvert = ouvert === s.id;
        return (
          <div className="pc-section" key={s.id}>
            <div
              className="pc-section-header"
              onClick={() => setOuvert(estOuvert ? null : s.id)}
              role="button"
              aria-expanded={estOuvert}
            >
              <span className="pc-num">{s.num}</span>
              <span className="pc-section-title">{s.titre}</span>
              <span className={`pc-chevron${estOuvert ? " ouvert" : ""}`}>▼</span>
            </div>
            {estOuvert && (
              <div className="pc-section-body">
                <s.contenu />
              </div>
            )}
          </div>
        );
      })}

      <div className="pc-footer">
        <p>Questions ? <a href={`mailto:${EMAIL}`}>{EMAIL}</a></p>
        <p style={{ marginTop: "0.5rem" }}>
          Voir aussi : <a href="/cookies">Politique cookies</a> · <a href="/mentions-legales">Mentions légales</a>
        </p>
      </div>
    </div>
  );
}
